import {Badge, Drawer} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Code} from 'src/design/atoms/Code';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import type {CSSProperties} from 'react';
import {useEffect, useRef, useState} from 'react';
import type {
  AssistantConversationTranscriptResponse,
  AssistantTranscriptMessageResponse,
} from 'src/api/ProactApiClient';
import {AssistantMarkdown} from 'src/app/assistant/AssistantMarkdown';
import {ChatChart} from 'src/app/assistant/Chart/ChatChart';
import {ChatTable} from 'src/app/assistant/Table/ChatTable';
import {MessageFeedbackSummary} from 'src/app/assistant/Feedback/MessageFeedbackSummary';
import {formatUtcDateTime} from 'src/shared/utils/formatters';
import {formatTokens, formatUsd} from './assistantUsageFormat';

export interface AssistantTranscriptDrawerProps {
  opened: boolean;
  onClose: () => void;
  transcript: AssistantConversationTranscriptResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  showInstance?: boolean;
  showCost?: boolean;
  /** Scrolled into view and ringed once the transcript renders. */
  focusMessageId?: number | null;
}

const FOCUS_RING_STYLE: CSSProperties = {
  boxShadow: '0 0 0 2px var(--mantine-color-blue-6)',
  borderRadius: 'var(--mantine-radius-md)',
  padding: 'var(--mantine-spacing-xs)',
};

const isAssistant = (role: string) => role.toLowerCase() === 'assistant';

const messageTokenSummary = (
  message: AssistantTranscriptMessageResponse
): string => {
  const parts: string[] = [];
  if (message.inputTokens) parts.push(`in ${formatTokens(message.inputTokens)}`);
  if (message.outputTokens)
    parts.push(`out ${formatTokens(message.outputTokens)}`);
  if (message.cacheReadInputTokens)
    parts.push(`cache read ${formatTokens(message.cacheReadInputTokens)}`);
  if (message.cacheCreationInputTokens)
    parts.push(`cache create ${formatTokens(message.cacheCreationInputTokens)}`);
  return parts.join(' · ');
};

const MetaRow = ({label, value}: {label: string; value: string}) => (
  <Group gap="xs" justify="space-between" wrap="nowrap">
    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
      {label}
    </Text>
    <Text size="sm">{value}</Text>
  </Group>
);

const TranscriptMessage = ({
  message,
}: {
  message: AssistantTranscriptMessageResponse;
}) => {
  const tokenSummary = messageTokenSummary(message);
  return (
    <Box>
      <Group gap="xs" mb={4} align="center">
        <Badge
          size="sm"
          variant="light"
          color={isAssistant(message.role) ? 'blue' : 'gray'}>
          {message.role}
        </Badge>
        <Text size="xs" c="dimmed">
          {formatUtcDateTime(message.createdAt)}
        </Text>
      </Group>

      {isAssistant(message.role) ? (
        <AssistantMarkdown content={message.content ?? ''} />
      ) : (
        <Text style={{whiteSpace: 'pre-wrap'}} size="sm">
          {message.content}
        </Text>
      )}

      {message.toolCalls && (
        <Box mt="xs">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={2}>
            Tool Calls
          </Text>
          <Code block style={{whiteSpace: 'pre-wrap', fontSize: 11}}>
            {message.toolCalls}
          </Code>
        </Box>
      )}

      {message.charts.length > 0 && (
        <Stack gap="md" mt="xs">
          {message.charts.map((chart, index) => (
            <ChatChart key={index} chart={chart} />
          ))}
        </Stack>
      )}

      {(message.tables?.length ?? 0) > 0 && (
        <Stack gap="md" mt="xs">
          {message.tables.map((table, index) => (
            <ChatTable key={index} table={table} />
          ))}
        </Stack>
      )}

      {message.feedback && (
        <Box mt="xs">
          <MessageFeedbackSummary feedback={message.feedback} />
        </Box>
      )}

      {message.truncated && (
        <Text size="xs" c="dimmed" fs="italic" mt={4}>
          Response was truncated.
        </Text>
      )}

      {message.effort && (
        <Text size="xs" c="dimmed" mt={4}>
          Effort {message.effort}
          {message.thinkHarder ? ' · Think harder' : ''}
        </Text>
      )}

      {tokenSummary && (
        <Text size="xs" c="dimmed" mt={4}>
          {tokenSummary}
        </Text>
      )}
    </Box>
  );
};

export const AssistantTranscriptDrawer = ({
  opened,
  onClose,
  transcript,
  isLoading,
  isError,
  showInstance,
  showCost = true,
  focusMessageId,
}: AssistantTranscriptDrawerProps) => {
  const focusRef = useRef<HTMLDivElement>(null);
  const [entered, setEntered] = useState(false);

  // The drawer slides in with a transform, so scroll only after it has entered and loaded.
  useEffect(() => {
    if (entered && focusMessageId && transcript) {
      focusRef.current?.scrollIntoView({block: 'center'});
    }
  }, [entered, focusMessageId, transcript]);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      onEnterTransitionEnd={() => setEntered(true)}
      onExitTransitionEnd={() => setEntered(false)}
      position="right"
      size="lg"
      title={<Text fw={600}>{transcript?.title ?? 'Conversation'}</Text>}>
      {isLoading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : isError ? (
        <Center py="xl">
          <Text c="red" size="sm">
            Failed to load transcript.
          </Text>
        </Center>
      ) : !transcript ? null : (
        <Stack gap="md">
          <Stack gap={4}>
            {showInstance && transcript.instanceName && (
              <MetaRow label="Instance" value={transcript.instanceName} />
            )}
            {transcript.userName && (
              <MetaRow label="User" value={transcript.userName} />
            )}
            <MetaRow
              label="Created"
              value={formatUtcDateTime(transcript.createdAt)}
            />
            <MetaRow
              label="Last Activity"
              value={formatUtcDateTime(transcript.lastActivityAt)}
            />
            <MetaRow
              label="Tokens"
              value={formatTokens(transcript.tokens.totalTokens)}
            />
            {showCost && (
              <MetaRow
                label="Est. Cost"
                value={formatUsd(transcript.tokens.estimatedCostUsd)}
              />
            )}
          </Stack>

          <Divider />

          {transcript.messages.length === 0 ? (
            <Center py="xl">
              <Text c="dimmed" size="sm">
                No messages in this conversation.
              </Text>
            </Center>
          ) : (
            <Stack gap="lg">
              {transcript.messages.map(message => {
                const focused = message.id === focusMessageId;
                return (
                  <Box
                    key={message.id}
                    ref={focused ? focusRef : undefined}
                    data-focused={focused || undefined}
                    style={focused ? FOCUS_RING_STYLE : undefined}>
                    <TranscriptMessage message={message} />
                  </Box>
                );
              })}
            </Stack>
          )}
        </Stack>
      )}
    </Drawer>
  );
};
